import {
  ButtonColor,
  ButtonSize,
  Combobox,
  Modal,
  ModalType,
  Option,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  CardReissue,
  Document,
  DocumentStorage,
  PosActivation,
  Product,
} from 'src/front/api/apiInstance';
import {
  CardActivationType,
  ModelError,
  ReferenceDataType,
  UploadDocumentResponse,
  UploadType,
} from 'src/front/api/generated';
import infoIcon from 'src/front/assets/icon_info.svg';

import usePRM from 'src/front/common/hooks/usePRM';
import displayPDF from 'src/front/common/utils/domUtils';

import { isInvalidFileError } from 'src/front/common/utils/errorUtils';
import { optionFormat } from 'src/front/common/utils/formUtils';
import AsyncButton from 'src/front/components/AsyncButton';

import {
  ButtonListRow,
  Container,
  FormElement,
  FormRow,
  InfoLabel,
  InfoRow,
  Separator,
  Subtitle,
  TableContainer,
  TableWrapper,
  Title,
  WarningContainer,
  WarningIcon,
  WarningText,
} from 'src/front/components/FormStyles';
import SuccessButtonText from 'src/front/components/SuccessButtonText';
import UploadDocumentForm, {
  UploadDocumentFormData,
} from 'src/front/components/UploadDocumentForm';

import {
  useCardReissueState,
  useCardSearchDispatch,
  useClearCardSearchDispatch,
  useClearDispatch,
  useSetSingleDocumentDispatch,
} from 'src/front/contexts/CardReissueContext';
import styled from 'styled-components';

const BigButton = styled(AsyncButton)`
  width: 500px;
  padding-left: 60px;

  text-align: left;
`;

interface CardSearchFormData {
  pos: Option | null;
  branch: Option | null;
  cardListType: Option | null;
}

const CARD_LIST_TYPES = {
  REISSUE: 'REISSUE',
  SENT: 'SENT',
  DIGITAL: 'DIGITAL',
};

const CARD_REISSUE_DOCUMENT_TYPE = 'REISSUE';

const defaultValues = {
  pos: null,
  branch: null,
  cardListType: null,
};

const cardsReissueTableHeader = [
  'cardReissue_cardNumber_text',
  'cardReissue_cardStatus_text',
  'cardReissue_cardExpireDate_text',
  'cardReissue_embossedName_text',
  'cardReissue_limit_text',
];

const cardsSentTableHeader = [
  'cardReissue_dateOfEntry_text',
  'cardReissue_dateOfApproval_text',
  'cardReissue_applicationStatus_text',
  'cardReissue_oldCardNumber_text',
  'cardReissue_newCardNumber_text',
  'cardReissue_embossedName_text',
  'cardReissue_previousLimit_label',
  'cardReissue_newLimit_label',
];

const getTableSubtitle = (cardType: string | undefined): string => {
  switch (cardType) {
    case CARD_LIST_TYPES.REISSUE:
      return 'cardReissue_cardsToReissue_text';
    case CARD_LIST_TYPES.SENT:
      return 'cardReissue_cardsSentForReissue_text';
    case CARD_LIST_TYPES.DIGITAL:
      return 'cardReissue_cardsDigitalPlastic_text';

    default:
      return ' ';
  }
};

export interface CardReissueTableRow extends TableRow {
  isNoNameChecked?: boolean;
  cardStatusCode?: string;
  cardSubStatusCode?: string;
  reissueReasonCode?: string;
  isCardActivationButtonVisible?: boolean;
  cardNumber?: string;
  cardAccountNumber?: string;
  expireDate?: string;
  currency?: string;
  embossedName?: string;
  embossedNameOther?: string;
  noNameCardId?: string;
  showPersonalIncome?: boolean;
  canReissued?: boolean;
}

interface Props {
  goToNextStep: () => void;
  goToFirstStep: () => void;
  setShowLastStep: Dispatch<SetStateAction<boolean>>;
  setShowIncomeLabels: Dispatch<SetStateAction<boolean>>;
}

const CardReissueCardSearch: FC<Props> = ({
  goToFirstStep,
  goToNextStep,
  setShowLastStep,
  setShowIncomeLabels,
}) => {
  const { customerSearchValues, cardSearchValues } = useCardReissueState();
  const { customerId, customerNumber, clientName } = customerSearchValues;
  const cardSearchDispatch = useCardSearchDispatch();
  const setSingleDocumentDispatch = useSetSingleDocumentDispatch();
  const clearSearchInput = useClearDispatch();
  const clearCardSearch = useClearCardSearchDispatch();
  const { t } = useTranslation();

  const [pointOfSales, setPointOfSales] = useState<Option[]>([]);

  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
  const [canReissued, setCanReissued] = useState<boolean>(true);
  const [selectedCard, setSelectedCard] = useState<CardReissueTableRow | undefined>(
    cardSearchValues.selectedCard
  );
  const [cardList, setCardList] = useState<TableRow[]>();
  const [cardPage, setCardPage] = useState<number>(cardSearchValues.cardPageNumber || 1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  const [showDocumentUploadForm, setShowDocumentUploadForm] = useState<boolean>(false);
  const [uploadedDocument, setUploadedDocument] = useState<UploadDocumentResponse>();
  const [innerDocument, setInnerDocument] = useState<UploadDocumentResponse>();
  const [hasDocumentUploadedWarning, setHasDocumentUploadedWarning] = useState<boolean>(false);
  const [isApplicationCompleted, setIsApplicationCompleted] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<ModelError>();

  const branches = usePRM(ReferenceDataType.POSSTORETYPE);
  const cardListTypes = usePRM(ReferenceDataType.REISSUETYPE);

  const { watch, register, setValue, getValues } = useForm<CardSearchFormData>({
    defaultValues: {
      ...defaultValues,
      pos: cardSearchValues.pos,
      branch: cardSearchValues.branch,
      cardListType: cardSearchValues.cardListType,
    },
  });

  const tableHeaders =
    watch('cardListType')?.value === CARD_LIST_TYPES.REISSUE ||
    watch('cardListType')?.value === CARD_LIST_TYPES.DIGITAL
      ? cardsReissueTableHeader
      : cardsSentTableHeader;

  useEffect(() => {
    register({ name: 'pos' });
    register({ name: 'branch' });
    register({ name: 'cardListType' });
  }, [register]);

  useEffect(() => {
    (async () => {
      const { data } = await Product.getPOSData();
      setPointOfSales(data.posDataList.map(optionFormat));
    })();
  }, []);

  const loadCardList = useCallback(
    async (
      pageNumber = cardPage,
      forceRefresh = false,
      type: string = watch('cardListType')?.value || ''
    ): Promise<void> => {
      try {
        let cards;
        let items;
        setIsLoadingTable(true);
        if (type === CARD_LIST_TYPES.REISSUE) {
          cards = await CardReissue.getCardsForReissue({
            customerNumber,
            customerTypeCode: cardSearchValues?.branch?.value || watch('branch')?.value || '',
            rowCountPerPage: 5,
            pageNumber: pageNumber || cardPage,
            refresh: !pageNumber || forceRefresh,
          });
          items = cards.data.cardList.map((card) => ({
            id: card.cardId,
            columns: [
              card.cardNumber,
              card.cardStatusValue,
              card.expireDate,
              card.embossedName,
              card.cardLimit,
            ],
            cardNumber: card.cardNumber,
            showPersonalIncome: card.showPersonalIncome,
            cardAccountNumber: card.cardAccountNumber,
            currency: card.currency,
            expireDate: card.expireDate,
            embossedName: card.embossedName,
            embossedNameOther: card.embossedNameOther,
            noNameCardId: card.noNameCardId,
            canReissued: card.canReissued,
          }));
        } else if (type === CARD_LIST_TYPES.SENT) {
          cards = await CardReissue.getCardsSentForReissue({
            customerNumber,
            posId: watch('pos')?.value || '',
            rowCountPerPage: 5,
            pageNumber: pageNumber || cardPage,
            refresh: !pageNumber || forceRefresh,
          });
          items = cards.data.cardList.map((card) => ({
            id: card.cardId,
            columns: [
              card.entryDate,
              card.approvalDate,
              card.applicationStatus,
              card.oldCardNumber,
              card.newCardNumber,
              card.embossedName,
              card.previousLimit || '',
              card.newLimit || '',
            ],
            cardNumber: card.newCardNumber,
            isCardActivationButtonVisible: card.isCardActivationButtonVisible,
          }));
        } else if (type === CARD_LIST_TYPES.DIGITAL) {
          cards = await CardReissue.getDigitalCardsForReissue({
            customerNumber,
            customerTypeCode: cardSearchValues?.branch?.value || watch('branch')?.value || '',
            rowCountPerPage: 5,
            pageNumber: pageNumber || cardPage,
            refresh: !pageNumber || forceRefresh,
          });
          items = cards.data.digitalCardList.map((card) => ({
            id: card.cardId,
            columns: [
              card.cardNumber,
              card.cardStatusValue,
              card.expireDate,
              card.embossedName,
              card.cardLimit,
            ],
            cardNumber: card.cardNumber,
            cardAccountNumber: card.cardAccountNumber,
            currency: card.currency,
            expireDate: card.expireDate,
            embossedName: card.embossedName,
            cardStatusCode: card.cardStatusCode,
            cardSubStatusCode: card.cardSubStatusCode,
            reissueReasonCode: card.reissueReasonCode,
            embossedNameOther: card.embossedNameOther,
            noNameCardId: card.noNameCardId,
          }));
        }

        setCardList(items || []);
        cards && setHasNextPage(cards.data.hasNextPage);
        setCardPage(pageNumber || 1);
        setIsLoadingTable(false);
      } catch (error) {
        // TODO (mertohancan): show error on modal or handle with error boundary
      }
    },
    [cardPage, cardSearchValues, customerNumber, watch]
  );

  const handleSelectCard = (card: CardReissueTableRow | undefined): void => {
    clearCardSearch();
    setSelectedCard(card);
    setUploadedDocument(undefined);
    setShowDocumentUploadForm(false);
    setHasDocumentUploadedWarning(false);
    setUploadError(undefined);
    setCanReissued(card?.canReissued ?? true);
  };

  const onCardSubmit = (): void => {
    setShowIncomeLabels(selectedCard?.showPersonalIncome || false);
    goToNextStep();
    const formData = getValues();
    cardSearchDispatch({
      cardSearchValues: {
        selectedCard,
        cardPageNumber: cardPage,
        ...formData,
      },
    });
  };

  const handleDownloadApplicationClick = async (): Promise<void> => {
    try {
      const { data } = await CardReissue.generateCardReissueSentForm({
        customerNumber,
        posId: watch('pos')?.value || '',
        clientName,
        cardId: selectedCard?.id || '',
        cardNumber: selectedCard?.cardNumber || '',
      });
      if (data.result === 'OK') {
        const { data: response } = await DocumentStorage.getDocument({
          documentId: data.documentId,
        });
        if (response.result === 'OK') {
          setShowDocumentUploadForm(true);
          displayPDF(response.documentData);
          const formData = getValues();
          cardSearchDispatch({
            cardSearchValues: {
              selectedCard,
              cardPageNumber: cardPage,
              ...formData,
            },
          });
        }
      }
    } catch (err) {
      // TODO (mertohancan): show error on modal or handle with error boundary
    }
  };

  const handlePosChange = async (posItem: Option | null): Promise<void> => {
    setValue('cardListType', null);
    setValue('pos', posItem);
    setSelectedCard(undefined);
    setCardList(undefined);
    if (posItem) {
      const {
        data: { storeCode },
      } = await Product.getPosDataDetails(posItem?.value || '');
      const branch = branches.find((item) => item.value === storeCode) || null;
      setValue('branch', branch);
    }
  };

  const handleBranchChange = (branch: Option | null): void => setValue('branch', branch);

  const handleCardListTypeChange = (cardListType: Option | null): void => {
    setSelectedCard(undefined);
    if (cardListType) {
      setShowLastStep(
        cardListType?.value === CARD_LIST_TYPES.REISSUE ||
          cardListType?.value === CARD_LIST_TYPES.DIGITAL
      );
      setValue('cardListType', cardListType);
      loadCardList(1, true, cardListType?.value || '');
      clearCardSearch();
    } else {
      setValue('cardListType', cardListType);
      setCardList(undefined);
      clearCardSearch();
    }
  };

  const onSubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    try {
      if (innerDocument?.documentId) {
        await Document.deleteDocument({ documentId: innerDocument?.documentId });
      }
      const { data } = await Document.uploadDocuments(
        formData.files && formData.files[0],
        CARD_REISSUE_DOCUMENT_TYPE,
        UploadType.REISSUESENT,
        formData.explanation,
        customerId
      );
      if (data.result === 'OK') {
        setInnerDocument(data);
        setUploadedDocument(data);
        setSingleDocumentDispatch({ documentFormData: data });
        setHasDocumentUploadedWarning(true);
      }
    } catch (error) {
      if (error && isInvalidFileError(error)) {
        setUploadError(error);
      }
    }
  };

  const handleActivateCard = async (): Promise<void> => {
    try {
      if (uploadedDocument) {
        const { result, message, sessionID, ...documentUploadData } = uploadedDocument;
        const { data } = await PosActivation.activateCard({
          cardId: selectedCard?.id || '',
          cardNumber: selectedCard?.cardNumber || '',
          customerNumber,
          cardActivationType: CardActivationType.REISSUE,
          documentId: uploadedDocument.documentId,
          documentUploadData,
        });

        if (data.result === 'OK') {
          setIsApplicationCompleted(true);
        }
      }
    } catch (err) {
      // TODO (mertohancan): show error on modal or handle with error boundary
    }
  };

  const handleApplicationSuccessClick = (): void => {
    setIsApplicationCompleted(false);
    clearSearchInput();
    goToFirstStep();
  };

  useEffect(() => {
    if (!cardList?.length && cardSearchValues.cardListType && !isLoadingTable) {
      loadCardList(cardPage, false, cardSearchValues.cardListType.value);
    }
  }, [cardList, cardPage, cardSearchValues.cardListType, isLoadingTable, loadCardList]);

  return (
    <Container>
      {isApplicationCompleted ? (
        <Modal
          description={t('cardReissue_forApprovedRecords_successMessage')}
          onCancel={handleApplicationSuccessClick}
          submitText={t('general_components_close_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}
      <Title>
        <Trans i18nKey="cardReissue_title" />
      </Title>
      <Separator />

      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="cardReissue_clientName_text" />:
        </InfoLabel>
        {clientName}
      </InfoRow>
      <Separator />
      <FormRow>
        <FormElement>
          <Combobox
            items={pointOfSales}
            label={t('cardReissue_pointOfSale_label')}
            name="pos"
            onChange={handlePosChange}
            value={watch('pos')}
            required
          />
        </FormElement>
        {watch('pos') ? (
          <FormElement>
            <Combobox
              items={branches}
              label={t('cardReissue_branch_label')}
              name="branch"
              onChange={handleBranchChange}
              value={watch('branch')}
              disabled
              required
            />
          </FormElement>
        ) : null}
      </FormRow>
      {watch('pos') ? (
        <FormRow>
          <FormElement>
            <Combobox
              items={cardListTypes}
              label={t('cardReissue_cardListType_label')}
              name="cardListType"
              onChange={handleCardListTypeChange}
              value={watch('cardListType')}
              required
            />
          </FormElement>
        </FormRow>
      ) : null}
      {cardList ? (
        <>
          <Separator />
          <Subtitle>
            <Trans i18nKey={getTableSubtitle(watch('cardListType')?.value)} />
          </Subtitle>

          <TableWrapper>
            <TableContainer>
              <Table
                emptyTableText={t('cardEntry_customerInfo_notFound_text')}
                hasNextPage={hasNextPage}
                headers={tableHeaders.map((head) => t(head))}
                items={cardList}
                loading={isLoadingTable}
                loadPageItems={loadCardList}
                onSelect={handleSelectCard}
                pageNumber={cardPage}
                selectedItem={selectedCard}
                clearItemOnPageChange
                withRadio
              />
            </TableContainer>
          </TableWrapper>
        </>
      ) : null}
      {selectedCard && selectedCard.isCardActivationButtonVisible ? (
        <>
          <Separator />
          <Title>
            <Trans i18nKey="cardReissue_printDocument_text" />
          </Title>

          <ButtonListRow>
            <FormElement>
              <BigButton
                color={ButtonColor.Secondary}
                onClick={handleDownloadApplicationClick}
                text={
                  <SuccessButtonText
                    isSuccess={showDocumentUploadForm}
                    label={t('cardReissue_download_button')}
                  />
                }
                type="button"
              />
            </FormElement>
          </ButtonListRow>

          <Separator />

          {showDocumentUploadForm ? (
            <>
              <UploadDocumentForm
                error={uploadError}
                handleContinue={handleActivateCard}
                setUploadedDocument={setUploadedDocument}
                submit={onSubmit}
                uploadedDocument={uploadedDocument}
              />
              {uploadedDocument && hasDocumentUploadedWarning && !isApplicationCompleted ? (
                <FormRow>
                  <WarningText>
                    <Trans i18nKey="cardObservation_afterUpload_errorMessage" />
                  </WarningText>
                </FormRow>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}

      {(watch('cardListType')?.value === CARD_LIST_TYPES.REISSUE ||
        watch('cardListType')?.value === CARD_LIST_TYPES.DIGITAL) &&
      selectedCard &&
      canReissued ? (
        <FormRow>
          <FormElement>
            <AsyncButton
              color={ButtonColor.Primary}
              onClick={onCardSubmit}
              size={ButtonSize.Medium}
              text={t('general_continue_button')}
              type="button"
            />
          </FormElement>
        </FormRow>
      ) : null}

      {watch('cardListType')?.value === CARD_LIST_TYPES.REISSUE && !canReissued ? (
        <WarningContainer>
          <WarningIcon src={infoIcon} />
          <WarningText>
            <Trans i18nKey="cardReissue_cardCannotBeReissued_text" />
          </WarningText>
        </WarningContainer>
      ) : null}
    </Container>
  );
};

export default CardReissueCardSearch;
